/**
 * The break between two trainings, as `Builder planu sekwencja.dc.html` draws it: a dashed tile
 * carrying the word "PRZERWA", the gap in words, and a −/+ pair under it.
 *
 * The tile only. The column's head — "DZIEŃ 2", "DNI 3–4", or nothing at a gap of zero — belongs to
 * the ROW and is drawn by `TapePane`, which draws the same two lines over every kind of column.
 *
 * ── This column is NOT a drop target, and that is substantive ────────────────────────────────
 * On the week a rest column is an empty WEEKDAY — a real destination with a coordinate of its own.
 * Here a break is the tail of the training before it (`DayDraft.restDaysAfter`): it has no slot
 * token, and `moveDayToPosition` has no coordinate that names it. "Between trainings 2 and 3" is
 * already expressible, and expressed, as "on training 3". So there is no `useDropTarget` here, no
 * `data-target`, and no `canDrop` — and the keyboard path's stops are the trainings alone, which is
 * `positionTargets`' own list. Nothing to restore later.
 *
 * ── The steppers are `aria-disabled` at a bound, never `disabled` ────────────────────────────
 * View ADR §12. The `disabled` attribute drops a control out of the tab order and takes the very
 * explanation it exists to deliver with it — a trainer who cannot work out why − does nothing at
 * zero is exactly the person the reason is for. Same pattern as the progression drawer's steppers.
 *
 * ── The value ANNOUNCES, and the sentence is what both buttons are described by ──────────────
 * Two things were missing and both were measured: pressing + twenty times moved the canvas's live
 * region not once, and neither button carried the value at all — a screen-reader user pressed
 * "Wydłuż przerwę po treningu Push A" and was told nothing about what the gap now was.
 *
 * The fix is one element doing both jobs. The value sits in an `<output>`, whose implicit
 * `role="status"` makes every press announce the new gap in words ("Przerwa po treningu Push A:
 * 3 dni, wskazówka dla klienta, nie termin."), and both buttons point `aria-describedby` at that
 * same sentence so the value is part of what they are.
 *
 * ── The refusal is DRAWN, not left on `title` ────────────────────────────────────────────────
 * A `title` reaches neither a touch user nor a keyboard user who never hovers. The column renders
 * ONE reason under the stepper — at most one can be in force — and both buttons are described by it.
 */

import { useId } from 'react';

import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';

import type { RestColumnView } from '../model/tapeViewModel';

import styles from './RestColumn.module.css';

interface RestColumnProps {
  readonly rest: RestColumnView;
  /**
   * Write the day's new gap. `null` when the plan cannot be edited — the column then draws its
   * value with no stepper at all, rather than two buttons that refuse.
   */
  readonly onChange: ((daySlotToken: string, days: number) => void) | null;
}

function Step({
  step,
  glyph,
  describedBy,
  onPress,
}: {
  readonly step: RestColumnView['decrease'];
  readonly glyph: string;
  /** The value sentence, and the refusal when there is one. Both are elements of the column. */
  readonly describedBy: string;
  readonly onPress: () => void;
}) {
  const reason = step.disabledReason;
  return (
    <button
      type="button"
      className={styles.step}
      aria-label={step.label}
      aria-disabled={reason !== null}
      aria-describedby={describedBy}
      onClick={() => {
        // Guarded here rather than by the `disabled` attribute — see the header. A press at the
        // bound does nothing, and the reason under the stepper is what says so.
        if (reason === null) onPress();
      }}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}

export function RestColumn({ rest, onChange }: RestColumnProps) {
  const valueId = useId();
  const reasonId = useId();
  // Both buttons are described by the value; when a refusal exists it is appended, so the two facts
  // arrive in the order a trainer needs them — what the gap is, then why it cannot move.
  const describedBy = rest.reason === null ? valueId : `${valueId} ${reasonId}`;

  return (
    <div className={styles.tile} data-zero={rest.isZero ? '' : undefined}>
      {/* The three parts the mock CENTRES, held together so the refusal below cannot move them.
        * They were three siblings of a centred column, and a refusal appearing among them shifted
        * the ± pair 33px up under the pointer that had just pressed it. See the stylesheet. */}
      <div className={styles.cluster}>
        {/* Horizontal, unlike the 04.08 canon's vertical strip. It stays a real text node — no
          * image, no per-letter spans — so it is selectable, searchable, translatable and announced
          * as one word, and `aria-hidden` because the `<output>` below says it in a sentence. */}
        <span className={styles.label} aria-hidden="true">
          {rest.label}
        </span>
        {/* An `<output>` rather than a `<p>`: its implicit `role="status"` is what makes a press
          * announce the new gap. The drawn value and the announced sentence are two renderings of
          * one fact — "2 dni" read alone, five columns deep, is five identical announcements. */}
        <output id={valueId} className={styles.value}>
          <span aria-hidden="true">{rest.value}</span>
          <span className={visuallyHidden.srOnly}>{rest.description}</span>
        </output>
        {/* No stepper at all on a plan that cannot be edited: two buttons that silently swallow
          * every press are worse than none, and they have no reason to offer either — the document
          * is read-only, not refusing. */}
        {onChange !== null && (
          <div className={styles.stepper}>
            <Step
              step={rest.decrease}
              glyph="−"
              describedBy={describedBy}
              onPress={() => {
                onChange(rest.daySlotToken, rest.decrease.value);
              }}
            />
            <Step
              step={rest.increase}
              glyph="+"
              describedBy={describedBy}
              onPress={() => {
                onChange(rest.daySlotToken, rest.increase.value);
              }}
            />
          </div>
        )}
      </div>
      {onChange !== null && rest.reason !== null && (
        <p id={reasonId} className={styles.reason}>
          {rest.reason}
        </p>
      )}
    </div>
  );
}
